import { useState, useMemo, useCallback, useEffect, useRef, useTransition, useDeferredValue } from 'react';
import type { NFTToken } from '@/hooks/portfolio/useNFTCollectionOptimized';
import type { NFTMetadata } from '@/util/nftMetadata';
import { type Address } from 'viem';
import { type ChainType } from '@/constants/chain-specific';
import { useBatchNFTMetadata } from '@/hooks/useNFTMetadata';
import { TOKEN_BATCH_SIZE, METADATA_BATCH_SIZE, PRELOAD_THRESHOLD } from '@/constants/ipfs';
interface UseOptimizedTokenLoadingProps {
  tokens: NFTToken[];
  currentPage: number;
  itemsPerPage: number;
  contractAddress: Address;
  chainId: ChainType;
  isERC1155?: boolean;
}
interface UseOptimizedTokenLoadingResult {
  sortedTokens: NFTToken[];
  currentPageTokens: NFTToken[];
  isLoadingMetadata: boolean;
  progress: {
    loaded: number;
    total: number;
  };
}

// Cache for metadata to persist across renders - using contract+token as key to prevent cross-collection contamination
const metadataCache = new Map<string, NFTMetadata>();

// Helper function to create unique cache key
const createCacheKey = (contractAddress: Address, tokenId: string): string => {
  return `${contractAddress.toLowerCase()}-${tokenId}`;
};
export function useOptimizedTokenLoading({
  tokens,
  currentPage,
  itemsPerPage,
  contractAddress,
  chainId,
  isERC1155 = false
}: UseOptimizedTokenLoadingProps): UseOptimizedTokenLoadingResult {
  const [isPending, startTransition] = useTransition();
  const deferredTokens = useDeferredValue(tokens);
  const [requestedTokenIds, setRequestedTokenIds] = useState<Set<string>>(new Set());
  const metadataMapRef = useRef<Map<string, NFTMetadata>>(new Map());

  // Sort tokens by ID first - this ensures a stable order during loading
  const sortedTokens = useMemo(() => {
    return [...deferredTokens].sort((a, b) => Number(a.tokenId) - Number(b.tokenId));
  }, [deferredTokens]);

  // Calculate current page range with next page preloading
  const currentPageRange = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const nextPageStart = endIndex;
    const nextPageEnd = nextPageStart + itemsPerPage;
    return {
      startIndex,
      endIndex,
      currentPageTokens: sortedTokens.slice(startIndex, endIndex),
      nextPageTokens: sortedTokens.slice(nextPageStart, nextPageEnd)
    };
  }, [sortedTokens, currentPage, itemsPerPage]);

  // Calculate which tokens need metadata
  const metadataNeededTokens = useMemo(() => {
    const {
      currentPageTokens,
      nextPageTokens
    } = currentPageRange;
    const allNeededTokens = [...currentPageTokens, ...nextPageTokens];
    return allNeededTokens.filter(token => {
      const tokenId = token.tokenId.toString();
      const cacheKey = createCacheKey(contractAddress, tokenId);
      return !token.metadata && !requestedTokenIds.has(cacheKey) && !metadataCache.has(cacheKey);
    });
  }, [currentPageRange, requestedTokenIds, contractAddress]);

  // Get metadata for needed tokens
  const {
    metadataMap,
    isLoading: isLoadingMetadata
  } = useBatchNFTMetadata(contractAddress, metadataNeededTokens.map(t => t.tokenId), chainId, {
    enabled: metadataNeededTokens.length > 0,
    batchSize: METADATA_BATCH_SIZE,
    isERC1155
  });

  // Update metadata cache and requested tokens
  useEffect(() => {
    if (!isLoadingMetadata && metadataMap.size > 0) {
      startTransition(() => {
        const newRequestedIds = new Set(requestedTokenIds);
        let hasNewIds = false;
        metadataMap.forEach((metadata, tokenId_0) => {
          const cacheKey_0 = createCacheKey(contractAddress, tokenId_0);
          if (!requestedTokenIds.has(cacheKey_0)) {
            // Debug logging for cross-collection contamination
            if (metadataCache.has(cacheKey_0)) {
              console.warn(`🔄 Overwriting existing cache entry for ${cacheKey_0}:`, {
                existing: metadataCache.get(cacheKey_0)?.name,
                new: metadata?.name
              });
            }
            metadataCache.set(cacheKey_0, metadata);
            newRequestedIds.add(cacheKey_0);
            hasNewIds = true;
          }
        });
        if (hasNewIds) {
          setRequestedTokenIds(newRequestedIds);
          metadataMapRef.current = new Map(metadataMap);
        }
      });
    }
  }, [metadataMap, isLoadingMetadata, requestedTokenIds, contractAddress]);

  // Calculate loading progress
  const progress = useMemo(() => ({
    loaded: requestedTokenIds.size,
    total: sortedTokens.length
  }), [requestedTokenIds, sortedTokens.length]);

  // Combine tokens with their metadata for current page
  const currentPageTokens_0 = useMemo(() => {
    return currentPageRange.currentPageTokens.map(token_0 => {
      const tokenId_1 = token_0.tokenId.toString();
      const cacheKey_1 = createCacheKey(contractAddress, tokenId_1);
      const cachedMetadata = metadataCache.get(cacheKey_1);
      const mapMetadata = metadataMapRef.current.get(tokenId_1);
      return {
        ...token_0,
        metadata: cachedMetadata || mapMetadata || token_0.metadata
      };
    });
  }, [currentPageRange.currentPageTokens, contractAddress]);
  return {
    sortedTokens,
    currentPageTokens: currentPageTokens_0,
    isLoadingMetadata: isLoadingMetadata || isPending,
    progress
  };
}